import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	FontIconName,
	TreeAccordion
} from "client/Components";
import { useToggleExpansion } from "client/Components/TreeAccordion/useToggleExpansion";
import { localize } from "client_legacy/ndaw/culture";
import { SelectedView } from "./HubManagementDialog";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { HubDataSource } from "client/Api/Hub/hub";
import { HubManagementViewModel } from "client/Api/Hub/hubSettings";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { never } from "client_legacy/ndaw/errors";

export type HubManagementDialogSidebarProps = {
	availableItems: readonly SidebarItem[];
	onSelectedViewChange: (selectedView: SelectedView) => void;
	selectedView: SelectedView | undefined;
};

export const HubManagementDialogSidebar = (props: HubManagementDialogSidebarProps): ReactElement => {
	const {
		availableItems,
		onSelectedViewChange,
		selectedView
	} = props;

	const initiallyExpandedSources = useMemo(
		(): HubDataSource[] => sidebarItems
			.filter(x => x.kind === "source")
			.map(x => x.source),
		[]
	);

	const { isExpanded, toggleExpansion } = useToggleExpansion(initiallyExpandedSources);

	const checkIsSelected = useCallback(
		(view: SelectedView): boolean => {
			return view === selectedView;
		},
		[selectedView]
	);

	return (
		<TreeAccordion trackingId="hub-management-dialog-sidebar">
			{availableItems.map(
				(x): ReactElement => {
					const key: SelectedView | HubDataSource = x.kind === "view"
						? x.view
						: x.source;

					return (
						<Item
							key={key}
							model={x}
							onSelectedViewChange={onSelectedViewChange}
							checkIsSelected={checkIsSelected}
							isExpanded={isExpanded}
							toggleExpansion={toggleExpansion}
						/>
					);
				}
			)}
		</TreeAccordion>
	);
};
HubManagementDialogSidebar.displayName = "HubManagementDialogSidebar";

type ItemProps = {
	model: SidebarItem;
	onSelectedViewChange: (selectedView: SelectedView) => void;
	checkIsSelected: (view: SelectedView) => boolean;
	isExpanded: (source: HubDataSource) => boolean;
	toggleExpansion: (source: HubDataSource) => void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		model,
		onSelectedViewChange,
		checkIsSelected,
		isExpanded,
		toggleExpansion
	} = props;

	switch (model.kind) {
		case "view":
			return (
				<TreeAccordion.Node>
					<TreeAccordion.Item
						trackingId={generateTrackingId("node", `|${model.view}`)}
						expansionToggle="hidden"
						title={model.title}
						onClick={() => onSelectedViewChange(model.view)}
						isSelected={checkIsSelected(model.view)}
					>
						{model.title}
					</TreeAccordion.Item>
				</TreeAccordion.Node>
			);
		case "source":
			return (
				<TreeAccordion.Node>
					<TreeAccordion.Item
						trackingId={generateTrackingId("node", `|${model.source}`)}
						icon={model.icon}
						expansionToggle="visible"
						isHeader
						title={model.title}
						isExpanded={isExpanded(model.source)}
						onClick={() => toggleExpansion(model.source)}
						onToggleExpansion={() => toggleExpansion(model.source)}
					>
						{model.title}
					</TreeAccordion.Item>
					<TreeAccordion.SubTree
						trackingId={generateTrackingId("sub-tree", `|${model.source}`)}
						isCollapsed={!isExpanded(model.source)}
					>
						{model.children
							.map(
								(subNode): ReactElement => (
									<TreeAccordion.Node key={`${model.source}-${subNode.view}`}>
										<TreeAccordion.Item
											trackingId={generateTrackingId("item", `|${model.source}-${subNode.view}`)}
											isSelected={checkIsSelected(subNode.view)}
											onClick={() => onSelectedViewChange(subNode.view)}
											indentionLevel={1}
											title={subNode.title}
										>
											{subNode.title}
										</TreeAccordion.Item>
									</TreeAccordion.Node>
								)
							)
						}
					</TreeAccordion.SubTree>
				</TreeAccordion.Node>
			);
		default:
			never(model);
	}
};
Item.displayName = "item";

export type SidebarSourceItem = {
	kind: "source";
	source: HubDataSource;
	title: string;
	icon: FontIconName;
	children: readonly SidebarViewItem[];
	isAvailable?: (permissions: HubManagementViewModel) => boolean;
};

export type SidebarViewItem = {
	kind: "view";
	view: SelectedView;
	title: string;
	isAvailable?: (permissions: HubManagementViewModel) => boolean;
};

export type SidebarItem = SidebarSourceItem | SidebarViewItem;

const sidebarItems: readonly SidebarItem[] = [
	{
		kind: "view",
		view: "settings",
		title: localize("_Settings"),
		isAvailable: (x) => x.canManageSettings
	},
	{
		kind: "source",
		source: HubDataSource.Announcement,
		title: localize("_Announcements"),
		icon: "bullhorn",
		children: [
			{
				kind: "view",
				view: "announcement.overview",
				title: localize("_Announcements")
			},
			{
				kind: "view",
				view: "announcement.category",
				title: localize("_Categories")
			},
			{
				kind: "view",
				view: "announcement.template",
				title: localize("_Templates"),
				isAvailable: (x) => x.hubItems
					.canManageTemplates
			}
		],
		isAvailable: (x) => x.hubItems
			.manageableKinds
			.includes(HubItemKind.Announcement)
	},
	{
		kind: "source",
		source: HubDataSource.Community,
		title: localize("_Communities"),
		icon: "users",
		children: [
			{
				kind: "view",
				view: "communities.settings",
				title: localize("_Settings"),
				isAvailable: (x) => x.communities
					.canManageSettings
			},
			{
				kind: "view",
				view: "communities",
				title: localize("_PostsAndCommunities"),
				isAvailable: (x) => {
					const { isEnabled, canManageCommunities } = x.communities;

					return isEnabled && canManageCommunities;
				}
			},
			{
				kind: "view",
				view: "communities.membershipRequests",
				title: localize("_MembershipRequests"),
				isAvailable: (x) => {
					const { isEnabled, canManageMembershipRequests } = x.communities;

					return isEnabled && canManageMembershipRequests;
				}
			}
		]
	},
	{
		kind: "source",
		source: HubDataSource.Event,
		title: localize("_Events"),
		icon: "calendar-star",
		children: [
			{
				kind: "view",
				view: "event.overview",
				title: localize("_Events")
			},
			{
				kind: "view",
				view: "event.category",
				title: localize("_Categories")
			},
			{
				kind: "view",
				view: "event.template",
				title: localize("_Templates"),
				isAvailable: (x) => x.hubItems
					.canManageTemplates
			}
		],
		isAvailable: (x) => x.hubItems
			.manageableKinds
			.includes(HubItemKind.Event)
	},
	{
		kind: "source",
		source: HubDataSource.KnowledgeItem,
		title: localize("_KnowledgeBase"),
		icon: "book",
		children: [
			{
				kind: "view",
				view: "knowledgeItem.overview",
				title: localize("_KnowledgeArticles")
			},
			{
				kind: "view",
				view: "knowledgeItem.category",
				title: localize("_Categories")
			},
			{
				kind: "view",
				view: "knowledgeItem.template",
				title: localize("_Templates"),
				isAvailable: (x) => x.hubItems
					.canManageTemplates
			}
		],
		isAvailable: (x) => x.hubItems
			.manageableKinds
			.includes(HubItemKind.KnowledgeItem)
	}
];

export function listAvailableSidebarItems(
	permissions: HubManagementViewModel
): readonly SidebarItem[] {
	const result: SidebarItem[] = [];

	for (const item of sidebarItems) {
		if (isNotNil(item.isAvailable) && !item.isAvailable(permissions)) {
			continue;
		}

		switch (item.kind) {
			case "view":
				result.push(item);
				break;
			case "source":
				const children: readonly SidebarViewItem[] = item.children
					.filter(
						(x) => isNil(x.isAvailable) || x.isAvailable(permissions)
					);

				if (children.length === 0) {
					continue;
				}

				result.push({
					...item,
					children
				});
				break;
			default:
				never(item);
		}
	}

	return result;
}